import compact from 'lodash/compact';
import styled from 'styled-components';
import { useState } from 'react';
import Cookie from 'js-cookie';

import { PriceBreakdownType } from 'shared/enums/PriceBreakdown';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { formatMoneyObject } from 'shared/utils/formatMoneyObject';
import { GlobalPricingLineItem } from 'checkout/components/GlobalPricingBreakdown/GlobalPricingLineItem';
import { CollapseBox, CollapseToggle } from 'shared/components/Collapse';
import {
  useCheckoutGlobalPricing,
  IGlobalPricingLineItem,
} from 'checkout/hooks/useCheckoutGlobalPricing';
import { FeatureFlags } from 'featureFlags/constants';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { STORAGE } from 'shared/enums/SitePreferences';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.GlobalPricingBreakdown.OrderSummaryPriceBreakdown';

interface IOrderSummaryPriceBreakdownProps {
  className?: string;
}

export const OrderSummaryPriceBreakdown = ({ className }: IOrderSummaryPriceBreakdownProps) => {
  const { order } = useCheckoutContext();
  const { getCheckoutGlobalPricingBreakdown, getSubtotalLineItem } = useCheckoutGlobalPricing();
  const { isAuthenticated } = useStoreSelector((state) => state.auth);

  const [expanded, setExpanded] = useState(false);

  const isCheckoutOffersEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_CHECKOUT_OFFERS);
  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_GUEST_CHECKOUT);

  const currencyCookie = Cookie.getJSON(STORAGE.CURRENCY);

  const internationalCheckoutNote = order?.internationalCheckoutNote;
  const priceBreakdownType = order?.priceBreakdownType;
  const productTemplateId = order?.product?.productTemplate?.id;
  const localizedTotal = order?.localizedFinalPriceCents;
  const subtotal = order?.localizedPriceCents;
  const isOffer = order?.wantId && isCheckoutOffersEnabled;
  const isUSD = currencyCookie === 'USD';

  const renderLineItems = (lineItems: IGlobalPricingLineItem[], options = {}) => {
    return compact(lineItems)
      .filter(
        (lineItem) =>
          lineItem.priceBreakdownTypes.includes(priceBreakdownType) ||
          lineItem.priceBreakdownTypes.includes(PriceBreakdownType.ALWAYS_SHOW),
      )
      .map((lineItem) => (
        <GlobalPricingLineItem
          key={lineItem.title}
          {...lineItem}
          options={options}
          productTemplateId={productTemplateId}
        />
      ));
  };

  const showInternationalCheckoutNote =
    internationalCheckoutNote && priceBreakdownType === PriceBreakdownType.SUBTOTAL_ONLY;

  const checkoutGlobalPricingBreakdown = getCheckoutGlobalPricingBreakdown(order);

  return (
    <OrderSummaryPriceBreakdown.Table className={className}>
      <OrderSummaryPriceBreakdown.CollapseToggle
        data-qa="order_summary_breakdown_toggle"
        title={
          showInternationalCheckoutNote
            ? t(
                `${TP}.estimatedTotalLineItemLabelWithNote`,
                `Estimated Total ({internationalCheckoutNote})`,
                { internationalCheckoutNote },
              )
            : t(`${TP}.estimatedTotalLineItemLabel`, 'Estimated Total')
        }
        expanded={expanded}
        onToggleClick={() => setExpanded((prev) => !prev)}
        subtitle={formatMoneyObject(localizedTotal)}
        disabled={isGuestCheckoutEnabled && !isAuthenticated}
      />
      <OrderSummaryPriceBreakdown.CollapseBox expanded={expanded}>
        {priceBreakdownType === PriceBreakdownType.DEFAULT && (
          <GlobalPricingLineItem key="subtotalLineItem" {...getSubtotalLineItem(subtotal)} />
        )}
        {renderLineItems([...checkoutGlobalPricingBreakdown.subtotal], { padded: true })}
        {renderLineItems([...checkoutGlobalPricingBreakdown.total], { padded: false })}
        {isOffer &&
          !isUSD &&
          renderLineItems([...checkoutGlobalPricingBreakdown.offers], { padded: false })}
      </OrderSummaryPriceBreakdown.CollapseBox>
    </OrderSummaryPriceBreakdown.Table>
  );
};

OrderSummaryPriceBreakdown.Table = styled.div`
  display: grid;
  grid-template-columns: [line-item] auto [price-start] auto [price-end];
  padding-bottom: 4px;
`;

OrderSummaryPriceBreakdown.CollapseToggle = styled(CollapseToggle)`
  grid-column-start: line-item;
  grid-column-end: price-end;
`;

OrderSummaryPriceBreakdown.CollapseBox = styled(CollapseBox)`
  grid-column-start: line-item;
  grid-column-end: price-end;

  > div {
    display: grid;
    grid-template-columns: 12px [sub-line-item] auto [price-start] auto [price-end];
  }
`;
